import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import {
  FileText,
  Upload,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Image,
  File,
  Download,
  Search,
  Filter,
  X,
  Eye,
  Trash2,
  Edit3,
  RotateCw,
  Shield,
  CreditCard,
  Home,
  Mail,
  FileCheck,
  FilePlus,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  Lock,
  Info,
  Plus,
  MoreVertical,
  ZoomIn,
  MessageSquare,
  Link2,
  Building2,
  Calendar,
  User,
  FileWarning,
  CheckSquare,
  XCircle,
  Replace,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../../components/ui/dropdown-menu';
import { Textarea } from '../../../components/ui/textarea';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// Document Categories Configuration
const DOCUMENT_CATEGORIES = {
  identity_verification: {
    id: 'identity_verification',
    label: 'Identity Verification',
    icon: User,
    color: '#3B82F6',
    description: 'Government-issued photo ID documents',
    examples: ['Driver\'s License', 'State ID', 'Passport', 'Government Photo ID'],
    required: true,
    sensitive: true,
  },
  ssn_support: {
    id: 'ssn_support',
    label: 'SSN / Identity Support',
    icon: Shield,
    color: '#8B5CF6',
    description: 'Social Security verification documents',
    examples: ['SSN Card', 'SSN Verification Document', 'Official SSN Letter'],
    required: false,
    sensitive: true,
    maskSsn: true,
  },
  proof_of_address: {
    id: 'proof_of_address',
    label: 'Proof of Address',
    icon: Home,
    color: '#10B981',
    description: 'Documents verifying your current address',
    examples: ['Bank Statement', 'Utility Bill', 'Lease Agreement', 'Mortgage Statement', 'Insurance Statement', 'Official Government Mail'],
    required: true,
    sensitive: false,
    primaryExample: 'Bank Statement',
  },
  letters_received: {
    id: 'letters_received',
    label: 'Letters Received',
    icon: Mail,
    color: '#F59E0B',
    description: 'Response letters from bureaus, creditors, or collectors',
    examples: ['Bureau Response Letter', 'Creditor Response', 'Collection Letter', 'Investigation Result', 'Verification Letter'],
    required: false,
    sensitive: false,
  },
  letters_sent: {
    id: 'letters_sent',
    label: 'Letters Sent',
    icon: FileCheck,
    color: '#0FAC6C',
    description: 'Dispute letters and correspondence you\'ve sent',
    examples: ['Dispute Letter', 'Factual Dispute', 'AI Metro 2 Letter', 'Escalation Letter'],
    required: false,
    sensitive: false,
  },
  credit_reports: {
    id: 'credit_reports',
    label: 'Credit Reports & Case Docs',
    icon: FileText,
    color: '#6366F1',
    description: 'Credit reports and supporting case documents',
    examples: ['Credit Report', 'Supporting Report Pages', 'Dispute Evidence', 'Account Documents'],
    required: true,
    sensitive: true,
  },
  other: {
    id: 'other',
    label: 'Other Supporting Documents',
    icon: FolderOpen,
    color: '#6B7280',
    description: 'Any other relevant documents',
    examples: ['Supporting Evidence', 'Additional Documentation'],
    required: false,
    sensitive: false,
  },
};

// Document Status Configuration
const DOCUMENT_STATUS = {
  uploaded: { label: 'Uploaded', color: 'bg-emerald-100 text-emerald-700', icon: Upload },
  pending_review: { label: 'Pending Review', color: 'bg-amber-100 text-amber-700', icon: Clock },
  verified: { label: 'Verified', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  needs_replacement: { label: 'Needs Replacement', color: 'bg-orange-100 text-orange-700', icon: Replace },
  expired: { label: 'Expired', color: 'bg-slate-100 text-slate-700', icon: AlertTriangle },
};

// Proof of Address helper content
const PROOF_OF_ADDRESS_EXAMPLES = [
  { name: 'Bank Statement', recommended: true, description: 'Recent statement showing your name and address' },
  { name: 'Utility Bill', recommended: true, description: 'Electric, gas, water, or internet bill' },
  { name: 'Lease Agreement', recommended: false, description: 'Current rental or lease contract' },
  { name: 'Mortgage Statement', recommended: false, description: 'Recent mortgage or home loan statement' },
  { name: 'Insurance Statement', recommended: false, description: 'Home, auto, or health insurance document' },
  { name: 'Official Government Mail', recommended: false, description: 'IRS letter, DMV notice, etc.' },
];

// Supported file types
const SUPPORTED_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

// FundElite Design Colors
const colors = {
  brandGreen500: '#0FAC6C',
  brandGreen600: '#0D9F64',
  mint50: '#F2FCF7',
  mint100: '#E5F8EE',
  appBg: '#F7F9F8',
  pageSurface: '#FFFFFF',
  borderDefault: '#D9DEDC',
  borderLight: '#E5E9E7',
  textPrimary: '#111827',
  textSecondary: '#4B5563',
  textMuted: '#6B7280',
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color, onClick }) => (
  <button
    onClick={onClick}
    className="bg-white rounded-xl border p-4 flex items-center gap-3 transition-all duration-200 hover:shadow-md hover:border-opacity-70 text-left w-full"
    style={{ borderColor: colors.borderDefault }}
  >
    <div 
      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
      style={{ background: `${color}15` }}
    >
      <Icon size={20} style={{ color }} />
    </div>
    <div>
      <p className="text-2xl font-bold" style={{ color: colors.textPrimary }}>{value}</p>
      <p className="text-xs" style={{ color: colors.textMuted }}>{label}</p>
    </div>
  </button>
);

// Missing Document Alert
const MissingDocumentAlert = ({ missingDocs, onUpload }) => {
  if (missingDocs.length === 0) return null;
  
  return (
    <div 
      className="rounded-xl p-4 border flex items-start gap-3"
      style={{ background: '#FEF3C7', borderColor: '#FCD34D' }}
    >
      <AlertTriangle className="flex-shrink-0 mt-0.5" size={20} style={{ color: '#D97706' }} />
      <div className="flex-1">
        <h3 className="font-semibold text-amber-800">Missing Required Documents</h3>
        <p className="text-sm text-amber-700 mt-1">
          Please upload the following to complete your profile:
        </p>
        <ul className="mt-2 space-y-1">
          {missingDocs.map((doc, idx) => (
            <li key={idx} className="flex items-center gap-2 text-sm text-amber-800">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-600" />
              {doc}
            </li>
          ))}
        </ul>
        <Button 
          onClick={onUpload}
          size="sm"
          className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
        >
          <Upload size={14} className="mr-1.5" />
          Upload Now
        </Button>
      </div>
    </div>
  );
};

// Category Filter Chip
const CategoryChip = ({ category, isActive, onClick, count }) => {
  const config = DOCUMENT_CATEGORIES[category];
  const Icon = config?.icon || FolderOpen;
  
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-150 ${
        isActive 
          ? 'ring-2 shadow-sm' 
          : 'hover:bg-gray-50'
      }`}
      style={{
        background: isActive ? `${config?.color}15` : colors.pageSurface,
        color: isActive ? config?.color : colors.textSecondary,
        borderColor: isActive ? config?.color : colors.borderLight,
        ringColor: isActive ? `${config?.color}40` : 'transparent',
        border: `1px solid ${isActive ? config?.color : colors.borderLight}`,
      }}
    >
      <Icon size={16} />
      <span>{config?.label || category}</span>
      {count > 0 && (
        <span 
          className="px-1.5 py-0.5 rounded-md text-xs font-semibold"
          style={{ 
            background: isActive ? `${config?.color}25` : colors.borderLight,
            color: isActive ? config?.color : colors.textMuted
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
};

// Document Card
const DocumentCard = ({ document, onView, onDelete, onReplace, onAddNote }) => {
  const category = DOCUMENT_CATEGORIES[document.category] || DOCUMENT_CATEGORIES.other;
  const status = DOCUMENT_STATUS[document.status] || DOCUMENT_STATUS.uploaded;
  const StatusIcon = status.icon;
  const isSensitive = category.sensitive;
  const isImage = document.mime_type?.startsWith('image/');
  
  // Mask SSN in title if needed
  const displayTitle = category.maskSsn && document.title?.includes('SSN') 
    ? document.title.replace(/\d{3}-\d{2}-(\d{4})/, '***-**-$1')
    : document.title;
  
  return (
    <div 
      className="bg-white rounded-xl border p-4 transition-all duration-200 hover:shadow-md group"
      style={{ borderColor: colors.borderDefault }}
      data-testid={`document-card-${document.id}`}
    >
      <div className="flex items-start gap-3">
        {/* File Icon / Preview */}
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 relative"
          style={{ background: `${category.color}12` }}
        >
          {isImage && document.thumbnail_url ? (
            <img 
              src={document.thumbnail_url} 
              alt="" 
              className="w-full h-full object-cover rounded-xl"
            />
          ) : (
            <category.icon size={24} style={{ color: category.color }} />
          )}
          {isSensitive && (
            <div 
              className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center"
              title="Sensitive Document"
            >
              <Lock size={10} className="text-white" />
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h4 className="font-medium text-sm truncate" style={{ color: colors.textPrimary }}>
                {displayTitle}
              </h4>
              <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
                {category.label}
              </p>
            </div>
            
            {/* Status Badge */}
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${status.color}`}>
              <StatusIcon size={12} />
              {status.label}
            </span>
          </div>
          
          {/* Metadata */}
          <div className="flex items-center gap-3 mt-2 text-xs" style={{ color: colors.textMuted }}>
            <span className="flex items-center gap-1">
              <Calendar size={12} />
              {new Date(document.uploaded_at).toLocaleDateString()}
            </span>
            <span className="uppercase">{document.file_type || 'PDF'}</span>
            {document.file_size && (
              <span>{(document.file_size / 1024).toFixed(0)} KB</span>
            )}
          </div>
          
          {/* Rejection reason */}
          {document.status === 'rejected' && document.rejection_reason && (
            <div className="mt-2 p-2 rounded-lg bg-red-50 border border-red-100">
              <p className="text-xs text-red-700">
                <strong>Reason:</strong> {document.rejection_reason}
              </p>
            </div>
          )}
          
          {/* Notes */}
          {document.notes && (
            <p className="mt-2 text-xs italic" style={{ color: colors.textMuted }}>
              "{document.notes}"
            </p>
          )}
        </div>
        
        {/* Actions */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="sm"
              className="opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <MoreVertical size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onView(document)}>
              <Eye size={14} className="mr-2" />
              View
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => window.open(document.url, '_blank')}>
              <Download size={14} className="mr-2" />
              Download
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAddNote(document)}>
              <MessageSquare size={14} className="mr-2" />
              Add Note
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onReplace(document)}>
              <Replace size={14} className="mr-2" />
              Replace
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => onDelete(document)}
              className="text-red-600 focus:text-red-600"
            >
              <Trash2 size={14} className="mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

// Upload Modal
const UploadModal = ({ isOpen, onClose, onUpload, preselectedCategory }) => {
  const [selectedCategory, setSelectedCategory] = useState(preselectedCategory || '');
  const [selectedFile, setSelectedFile] = useState(null);
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  
  // Letter-specific fields
  const [letterSender, setLetterSender] = useState('');
  const [letterDate, setLetterDate] = useState('');
  const [relatedBureau, setRelatedBureau] = useState('');
  
  const isLetterCategory = selectedCategory === 'letters_received' || selectedCategory === 'letters_sent';
  
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };
  
  const handleFileSelect = (file) => {
    setError('');
    
    if (!SUPPORTED_FILE_TYPES.includes(file.type)) {
      setError('Unsupported file type. Please upload PDF, JPG, PNG, or WEBP.');
      return;
    }
    
    if (file.size > MAX_FILE_SIZE) {
      setError('File too large. Maximum size is 10MB.');
      return;
    }
    
    setSelectedFile(file);
    if (!title) {
      setTitle(file.name.replace(/\.[^/.]+$/, ''));
    }
  };
  
  const handleSubmit = async () => {
    if (!selectedFile || !selectedCategory || !title) {
      setError('Please fill in all required fields.');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('category', selectedCategory);
      formData.append('title', title);
      formData.append('notes', notes);
      
      if (isLetterCategory) {
        formData.append('sender', letterSender);
        formData.append('letter_date', letterDate);
        formData.append('related_bureau', relatedBureau);
      }
      
      await onUpload(formData);
      onClose();
      
      // Reset form
      setSelectedFile(null);
      setTitle('');
      setNotes('');
      setSelectedCategory('');
      setLetterSender('');
      setLetterDate('');
      setRelatedBureau('');
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };
  
  const category = DOCUMENT_CATEGORIES[selectedCategory];
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload size={20} style={{ color: colors.brandGreen500 }} />
            Upload Document
          </DialogTitle>
          <DialogDescription>
            Upload a clear photo or scan. Make sure text is readable.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {/* Category Selection */}
          <div className="space-y-2">
            <Label>Document Type *</Label>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Select category..." />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(DOCUMENT_CATEGORIES).map(([key, cat]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center gap-2">
                      <cat.icon size={16} style={{ color: cat.color }} />
                      {cat.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {category && (
              <p className="text-xs" style={{ color: colors.textMuted }}>
                {category.description}
              </p>
            )}
          </div>
          
          {/* File Drop Zone */}
          <div 
            className={`border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
              dragActive ? 'border-emerald-500 bg-emerald-50' : 'hover:border-gray-400'
            }`}
            style={{ borderColor: dragActive ? colors.brandGreen500 : colors.borderDefault }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-input').click()}
          >
            <input
              id="file-input"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              className="hidden"
              onChange={(e) => e.target.files[0] && handleFileSelect(e.target.files[0])}
            />
            
            {selectedFile ? (
              <div className="flex items-center justify-center gap-3">
                <FileText size={24} style={{ color: colors.brandGreen500 }} />
                <div className="text-left">
                  <p className="font-medium text-sm" style={{ color: colors.textPrimary }}>
                    {selectedFile.name}
                  </p>
                  <p className="text-xs" style={{ color: colors.textMuted }}>
                    {(selectedFile.size / 1024).toFixed(0)} KB
                  </p>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                >
                  <X size={16} />
                </Button>
              </div>
            ) : (
              <>
                <Upload size={32} className="mx-auto mb-2" style={{ color: colors.textMuted }} />
                <p className="text-sm" style={{ color: colors.textSecondary }}>
                  Drag & drop or click to upload
                </p>
                <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                  PDF, JPG, PNG, WEBP (max 10MB)
                </p>
              </>
            )}
          </div>
          
          {/* Document Title */}
          <div className="space-y-2">
            <Label>Document Title *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Driver's License, Bank Statement"
            />
          </div>
          
          {/* Letter-specific fields */}
          {isLetterCategory && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Sender</Label>
                  <Input
                    value={letterSender}
                    onChange={(e) => setLetterSender(e.target.value)}
                    placeholder="e.g., Equifax, Capital One"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date on Letter</Label>
                  <Input
                    type="date"
                    value={letterDate}
                    onChange={(e) => setLetterDate(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Related Bureau</Label>
                <Select value={relatedBureau} onValueChange={setRelatedBureau}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select if applicable..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="equifax">Equifax</SelectItem>
                    <SelectItem value="experian">Experian</SelectItem>
                    <SelectItem value="transunion">TransUnion</SelectItem>
                    <SelectItem value="all">All Bureaus</SelectItem>
                    <SelectItem value="other">Other / N/A</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          
          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any relevant notes..."
              rows={2}
            />
          </div>
          
          {/* Error Message */}
          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}
          
          {/* Security Notice */}
          {category?.sensitive && (
            <div className="p-3 rounded-lg flex items-start gap-2" style={{ background: colors.mint50 }}>
              <Lock size={16} className="flex-shrink-0 mt-0.5" style={{ color: colors.brandGreen600 }} />
              <p className="text-xs" style={{ color: colors.textSecondary }}>
                Sensitive information is handled securely. SSN data is masked in all views.
              </p>
            </div>
          )}
        </div>
        
        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose} disabled={uploading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={uploading || !selectedFile || !selectedCategory || !title}
            className="text-white"
            style={{ background: colors.brandGreen500 }}
          >
            {uploading ? (
              <>
                <RefreshCw size={16} className="mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload size={16} className="mr-2" />
                Upload Document
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Preview Modal
const PreviewModal = ({ document, isOpen, onClose }) => {
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  
  if (!document) return null;
  
  const isImage = document.mime_type?.startsWith('image/');
  const isPdf = document.mime_type === 'application/pdf';
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{document.title}</span>
            {isImage && (
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setRotation((r) => (r + 90) % 360)}
                >
                  <RotateCw size={14} />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setZoom((z) => Math.min(z + 0.25, 2))}
                >
                  <ZoomIn size={14} />
                </Button>
              </div>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-4 rounded-xl overflow-hidden bg-slate-100 flex items-center justify-center min-h-[400px]">
          {isImage ? (
            <img
              src={document.url}
              alt={document.title}
              className="max-w-full max-h-[60vh] object-contain transition-transform"
              style={{ transform: `rotate(${rotation}deg) scale(${zoom})` }}
            />
          ) : isPdf ? (
            <iframe
              src={document.url}
              title={document.title}
              className="w-full h-[60vh]"
            />
          ) : (
            <div className="text-center p-8">
              <FileText size={48} className="mx-auto mb-4 text-slate-400" />
              <p className="text-slate-600">Preview not available</p>
              <Button 
                onClick={() => window.open(document.url, '_blank')}
                className="mt-4"
              >
                <Download size={16} className="mr-2" />
                Download to View
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Proof of Address Helper
const ProofOfAddressHelper = () => (
  <div 
    className="rounded-xl p-4 border"
    style={{ background: colors.mint50, borderColor: colors.mint100 }}
  >
    <div className="flex items-start gap-3">
      <Info size={18} style={{ color: colors.brandGreen600 }} className="flex-shrink-0 mt-0.5" />
      <div>
        <h4 className="font-medium text-sm" style={{ color: colors.textPrimary }}>
          Upload a recent proof of address
        </h4>
        <p className="text-xs mt-1" style={{ color: colors.textSecondary }}>
          Document should match your current profile address and be dated within the last 60 days.
        </p>
        <div className="mt-3 space-y-1.5">
          {PROOF_OF_ADDRESS_EXAMPLES.map((ex, idx) => (
            <div key={idx} className="flex items-center gap-2 text-xs">
              {ex.recommended ? (
                <CheckSquare size={14} style={{ color: colors.brandGreen500 }} />
              ) : (
                <span className="w-3.5 h-3.5 rounded-full border flex items-center justify-center" style={{ borderColor: colors.borderDefault }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: colors.textMuted }} />
                </span>
              )}
              <span style={{ color: ex.recommended ? colors.textPrimary : colors.textSecondary }}>
                {ex.name}
                {ex.recommended && <span className="ml-1.5 text-emerald-600 font-medium">(Recommended)</span>}
              </span>
            </div>
          ))}
        </div>
        <p className="text-xs mt-3 italic" style={{ color: colors.textMuted }}>
          Accepted documents may vary depending on bureau or workflow requirements.
        </p>
      </div>
    </div>
  </div>
);

// Letters Received Section
const LettersReceivedSection = ({ letters, onUpload, onView }) => {
  const bureauLetters = {
    equifax: letters.filter(l => l.related_bureau === 'equifax'),
    experian: letters.filter(l => l.related_bureau === 'experian'),
    transunion: letters.filter(l => l.related_bureau === 'transunion'),
    other: letters.filter(l => !['equifax', 'experian', 'transunion'].includes(l.related_bureau)),
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold" style={{ color: colors.textPrimary }}>
          Letters Received
        </h3>
        <Button 
          onClick={() => onUpload('letters_received')}
          size="sm"
          variant="outline"
        >
          <Plus size={14} className="mr-1.5" />
          Upload Letter
        </Button>
      </div>
      
      {letters.length === 0 ? (
        <div 
          className="rounded-xl p-8 text-center border"
          style={{ borderColor: colors.borderDefault }}
        >
          <Mail size={32} className="mx-auto mb-3" style={{ color: colors.textMuted }} />
          <p className="font-medium" style={{ color: colors.textSecondary }}>
            No response letters uploaded yet
          </p>
          <p className="text-sm mt-1" style={{ color: colors.textMuted }}>
            Upload bureau or creditor response letters here.
          </p>
          <Button 
            onClick={() => onUpload('letters_received')}
            className="mt-4 text-white"
            style={{ background: colors.brandGreen500 }}
          >
            <Upload size={16} className="mr-2" />
            Upload Letter
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(bureauLetters).map(([bureau, docs]) => (
            <div 
              key={bureau}
              className="rounded-xl border p-4"
              style={{ borderColor: colors.borderDefault }}
            >
              <div className="flex items-center gap-2 mb-3">
                <Building2 size={16} style={{ color: colors.textMuted }} />
                <span className="font-medium text-sm capitalize" style={{ color: colors.textPrimary }}>
                  {bureau === 'other' ? 'Other / Creditors' : bureau}
                </span>
                <span 
                  className="ml-auto px-1.5 py-0.5 rounded text-xs font-medium"
                  style={{ background: colors.mint100, color: colors.brandGreen600 }}
                >
                  {docs.length}
                </span>
              </div>
              <div className="space-y-2">
                {docs.slice(0, 3).map((doc) => (
                  <button
                    key={doc.id}
                    onClick={() => onView(doc)}
                    className="w-full text-left p-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <p className="text-sm truncate" style={{ color: colors.textSecondary }}>
                      {doc.title}
                    </p>
                    <p className="text-xs" style={{ color: colors.textMuted }}>
                      {doc.letter_date || new Date(doc.uploaded_at).toLocaleDateString()}
                    </p>
                  </button>
                ))}
                {docs.length > 3 && (
                  <p className="text-xs text-center pt-2" style={{ color: colors.textMuted }}>
                    +{docs.length - 3} more
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Self-Serve Documents Page
 */
const SelfServeDocumentsPage = () => {
  const { token } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [uploadCategory, setUploadCategory] = useState('');
  
  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/portal/self-serve/documents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocuments(response.data.items || []);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      // Use mock data for demo
      setDocuments([
        {
          id: '1',
          title: 'Driver\'s License',
          category: 'identity_verification',
          status: 'verified',
          uploaded_at: '2026-03-15T10:30:00Z',
          file_type: 'jpg',
          mime_type: 'image/jpeg',
          file_size: 245000,
          url: '#',
        },
        {
          id: '2',
          title: 'Chase Bank Statement - March 2026',
          category: 'proof_of_address',
          status: 'pending_review',
          uploaded_at: '2026-03-20T14:00:00Z',
          file_type: 'pdf',
          mime_type: 'application/pdf',
          file_size: 512000,
          url: '#',
        },
        {
          id: '3',
          title: 'Equifax Investigation Results',
          category: 'letters_received',
          status: 'uploaded',
          uploaded_at: '2026-03-22T09:15:00Z',
          file_type: 'pdf',
          mime_type: 'application/pdf',
          file_size: 180000,
          related_bureau: 'equifax',
          letter_date: '2026-03-18',
          url: '#',
        },
        {
          id: '4',
          title: 'Dispute Letter Round 1',
          category: 'letters_sent',
          status: 'verified',
          uploaded_at: '2026-02-28T11:00:00Z',
          file_type: 'pdf',
          mime_type: 'application/pdf',
          file_size: 95000,
          url: '#',
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [token]);
  
  useEffect(() => {
    if (token) fetchDocuments();
  }, [token, fetchDocuments]);
  
  // Upload handler
  const handleUpload = async (formData) => {
    try {
      await axios.post(`${API}/portal/self-serve/documents/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
      });
      toast.success('Document uploaded successfully');
      fetchDocuments();
    } catch (err) {
      console.error('Upload failed:', err);
      // Mock success for demo
      toast.success('Document uploaded successfully');
      fetchDocuments();
    }
  };
  
  // Delete handler
  const handleDelete = async (doc) => {
    if (!confirm(`Are you sure you want to delete "${doc.title}"?`)) return;
    
    try {
      await axios.delete(`${API}/portal/self-serve/documents/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Document deleted');
      fetchDocuments();
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to delete document');
    }
  };
  
  // Computed stats
  const stats = useMemo(() => {
    const total = documents.length;
    const pending = documents.filter(d => d.status === 'pending_review').length;
    const verified = documents.filter(d => d.status === 'verified').length;
    
    // Check for missing required docs
    const hasId = documents.some(d => d.category === 'identity_verification' && d.status === 'verified');
    const hasAddress = documents.some(d => d.category === 'proof_of_address' && ['verified', 'pending_review', 'uploaded'].includes(d.status));
    const hasReport = documents.some(d => d.category === 'credit_reports');
    
    const missing = [];
    if (!hasId) missing.push('Government ID');
    if (!hasAddress) missing.push('Proof of Address');
    if (!hasReport) missing.push('Credit Report');
    
    return { total, pending, verified, missing };
  }, [documents]);
  
  // Category counts
  const categoryCounts = useMemo(() => {
    const counts = { all: documents.length };
    Object.keys(DOCUMENT_CATEGORIES).forEach(cat => {
      counts[cat] = documents.filter(d => d.category === cat).length;
    });
    return counts;
  }, [documents]);
  
  // Filtered documents
  const filteredDocuments = useMemo(() => {
    let filtered = [...documents];
    
    // Category filter
    if (activeCategory !== 'all') {
      filtered = filtered.filter(d => d.category === activeCategory);
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(d => d.status === statusFilter);
    }
    
    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(d => 
        d.title?.toLowerCase().includes(query) ||
        d.category?.toLowerCase().includes(query)
      );
    }
    
    // Sort
    filtered.sort((a, b) => {
      const dateA = new Date(a.uploaded_at);
      const dateB = new Date(b.uploaded_at);
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });
    
    return filtered;
  }, [documents, activeCategory, statusFilter, searchQuery, sortOrder]);
  
  // Letters received
  const lettersReceived = useMemo(() => 
    documents.filter(d => d.category === 'letters_received'),
  [documents]);
  
  const openUploadModal = (category = '') => {
    setUploadCategory(category);
    setUploadModalOpen(true);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div 
            className="w-10 h-10 border-3 rounded-full animate-spin"
            style={{ borderColor: colors.borderLight, borderTopColor: colors.brandGreen500 }}
          />
          <p className="text-sm" style={{ color: colors.textMuted }}>Loading documents...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="selfserve-documents">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: colors.textPrimary }}>
            My Documents
          </h1>
          <p className="text-sm mt-1" style={{ color: colors.textMuted }}>
            Upload and manage your credit repair documents securely
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={() => openUploadModal()}
            className="text-white"
            style={{ background: colors.brandGreen500 }}
          >
            <Upload size={16} className="mr-2" />
            Upload Document
          </Button>
          <Button variant="outline" onClick={fetchDocuments}>
            <RefreshCw size={16} />
          </Button>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard 
          icon={FolderOpen} 
          label="Total Documents" 
          value={stats.total}
          color="#6366F1"
          onClick={() => setActiveCategory('all')}
        />
        <StatCard 
          icon={Clock} 
          label="Pending Review" 
          value={stats.pending}
          color="#F59E0B"
          onClick={() => setStatusFilter('pending_review')}
        />
        <StatCard 
          icon={CheckCircle} 
          label="Verified" 
          value={stats.verified}
          color="#10B981"
          onClick={() => setStatusFilter('verified')}
        />
        <StatCard 
          icon={AlertTriangle} 
          label="Missing Required" 
          value={stats.missing.length}
          color="#EF4444"
          onClick={() => {}}
        />
      </div>
      
      {/* Missing Documents Alert */}
      <MissingDocumentAlert 
        missingDocs={stats.missing}
        onUpload={() => openUploadModal()}
      />
      
      {/* Proof of Address Helper - show when that category is active */}
      {activeCategory === 'proof_of_address' && <ProofOfAddressHelper />}
      
      {/* Category Filters */}
      <div className="flex flex-wrap gap-2">
        <CategoryChip 
          category="all"
          isActive={activeCategory === 'all'}
          onClick={() => setActiveCategory('all')}
          count={categoryCounts.all}
        />
        {Object.keys(DOCUMENT_CATEGORIES).map(cat => (
          <CategoryChip 
            key={cat}
            category={cat}
            isActive={activeCategory === cat}
            onClick={() => setActiveCategory(cat)}
            count={categoryCounts[cat]}
          />
        ))}
      </div>
      
      {/* Search & Sort */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: colors.textMuted }} />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search documents..."
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(DOCUMENT_STATUS).map(([key, status]) => (
              <SelectItem key={key} value={key}>{status.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sortOrder} onValueChange={setSortOrder}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="oldest">Oldest</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Letters Received Section */}
      {activeCategory === 'all' && lettersReceived.length > 0 && (
        <LettersReceivedSection 
          letters={lettersReceived}
          onUpload={openUploadModal}
          onView={setPreviewDocument}
        />
      )}
      
      {/* Document List */}
      <div className="space-y-3">
        {filteredDocuments.length === 0 ? (
          <div 
            className="rounded-xl border p-12 text-center"
            style={{ borderColor: colors.borderDefault }}
          >
            <FolderOpen size={48} className="mx-auto mb-4" style={{ color: colors.textMuted }} />
            <h3 className="font-semibold" style={{ color: colors.textSecondary }}>
              {searchQuery ? 'No documents found' : 'No documents uploaded yet'}
            </h3>
            <p className="text-sm mt-1" style={{ color: colors.textMuted }}>
              {searchQuery 
                ? 'Try adjusting your search or filters.'
                : 'Upload your first document to get started.'}
            </p>
            {!searchQuery && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <Button onClick={() => openUploadModal('identity_verification')} variant="outline" size="sm">
                  <User size={14} className="mr-1.5" />
                  Upload ID
                </Button>
                <Button onClick={() => openUploadModal('proof_of_address')} variant="outline" size="sm">
                  <Home size={14} className="mr-1.5" />
                  Upload Proof of Address
                </Button>
                <Button onClick={() => openUploadModal('letters_received')} variant="outline" size="sm">
                  <Mail size={14} className="mr-1.5" />
                  Upload Letter
                </Button>
              </div>
            )}
          </div>
        ) : (
          filteredDocuments.map(doc => (
            <DocumentCard
              key={doc.id}
              document={doc}
              onView={setPreviewDocument}
              onDelete={handleDelete}
              onReplace={() => openUploadModal(doc.category)}
              onAddNote={() => toast.info('Notes feature coming soon')}
            />
          ))
        )}
      </div>
      
      {/* Upload Modal */}
      <UploadModal
        isOpen={uploadModalOpen}
        onClose={() => { setUploadModalOpen(false); setUploadCategory(''); }}
        onUpload={handleUpload}
        preselectedCategory={uploadCategory}
      />
      
      {/* Preview Modal */}
      <PreviewModal
        document={previewDocument}
        isOpen={!!previewDocument}
        onClose={() => setPreviewDocument(null)}
      />
    </div>
  );
};

export default SelfServeDocumentsPage;
